<template>
  <div class="flex min-h-0 flex-col bg-white" :class="sortedRows.length ? 'max-h-[500px]' : ''">
    <div v-if="error" class="m-5 rounded-lg border border-red-light bg-red-tint p-3 text-sm text-red-dark" role="alert">
      {{ error }} <button type="button" class="ml-2 font-semibold hover:underline" @click="load">Retry</button>
    </div>
    <LoadingIndicator v-else-if="loading" message="Loading meeting intelligence..." />
    <div v-else-if="!sortedRows.length" class="px-5 py-6 text-sm text-growth-600">
      No intelligence findings are grounded in this meeting yet.
    </div>
    <div v-else class="min-h-0 flex-1 overflow-auto">
      <table class="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col class="w-[14%]" />
          <col class="w-[50%]" />
          <col class="w-[12%]" />
          <col class="w-[20%]" />
          <col class="w-[4%]" />
        </colgroup>
        <thead class="sticky top-0 z-10 bg-growth-10">
          <tr class="border-b border-growth-50">
            <th v-for="column in columns" :key="column.key" class="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-growth-600" :class="column.edgeClass">
              <button v-if="column.sortable" type="button" class="inline-flex items-center gap-1.5 text-left focus:outline-none" @click="toggleSort(column.key)">
                {{ column.label }}
                <font-awesome-icon v-if="sort.key === column.key" :icon="['fas', sort.direction === 'asc' ? 'sort-up' : 'sort-down']" class="h-3 w-3" />
              </button>
              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in sortedRows" :key="row.id">
            <tr class="border-b border-growth-50 align-top">
              <td class="py-2.5 pl-4 pr-3">
                <span class="inline-flex items-center gap-2 text-xs font-semibold text-growth-800">
                  <span class="h-2 w-2 shrink-0 rounded-full" :class="typeDotClass(row.type)"></span>{{ typeLabel(row.type) }}
                </span>
              </td>
              <td class="px-3 py-2.5">
                <router-link :to="trackerLink(row, row.id)" class="sensitive font-semibold leading-5 text-growth-900 hover:underline">{{ row.title }}</router-link>
                <p v-if="row.description" class="mt-0.5 text-xs leading-4 text-growth-600 line-clamp-2">{{ row.description }}</p>
              </td>
              <td class="px-3 py-2.5">
                <div class="flex flex-wrap items-center gap-1">
                  <span class="inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold" :class="statusClass(row.status)">{{ label(row.status) }}</span>
                  <span class="inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold" :class="changeClass(row.change)" :title="changeTitle(row.change)">{{ changeLabel(row.change) }}</span>
                </div>
              </td>
              <td class="px-3 py-2.5 text-xs text-growth-600">
                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <ET_TrajectoryBadge v-if="row.type === 'initiative' && row.trajectory" :trajectory="row.trajectory" size="sm" />
                  <span v-else-if="row.severity" class="rounded px-1.5 py-0.5 text-[11px] font-semibold" :class="severityStyle(row.severity)">{{ label(row.severity) }} severity</span>
                </div>
                <div class="mt-1 whitespace-nowrap">{{ discussionSummary(row) }}</div>
              </td>
              <td class="py-2 pl-2 pr-4 text-right">
                <button type="button" class="disclosure-button" :aria-expanded="isExpanded(row.id)" :title="isExpanded(row.id) ? 'Hide details' : 'Show details'" @click="toggleExpanded(row.id)">
                  <font-awesome-icon :icon="['fas', isExpanded(row.id) ? 'chevron-up' : 'chevron-down']" class="h-3 w-3" />
                </button>
              </td>
            </tr>
            <tr v-if="isExpanded(row.id)" class="border-b border-growth-50 bg-growth-10">
              <td colspan="5" class="px-4 py-3">
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div class="space-y-3">
                    <ET_NextStep v-if="row.next_step" :text="row.next_step" :owner="row.owner" size="sm" />
                    <div v-if="row.owner || row.due_date" class="text-xs text-growth-600">
                      <span v-if="row.owner">Owner <span class="sensitive font-semibold text-growth-800">{{ row.owner }}</span></span>
                      <span v-if="row.owner && row.due_date"> · </span>
                      <span v-if="row.due_date">Due <span class="font-semibold text-growth-800">{{ formatDate(row.due_date) }}</span></span>
                    </div>
                    <div v-if="row.relationships.length">
                      <p class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-growth-600">Relationships</p>
                      <ul class="space-y-0.5">
                        <li v-for="(rel, i) in row.relationships" :key="i" class="text-xs text-growth-800">
                          <span class="text-growth-600">{{ relationshipLabel(rel) }}</span>
                          <router-link :to="trackerLink(row, rel.node_id)" class="sensitive font-semibold hover:underline">{{ rel.title }}</router-link>
                          <span class="text-growth-600"> · {{ typeLabel(rel.type) }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <p class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-growth-600">Evidence from this meeting</p>
                    <ul v-if="row.evidence.length" class="space-y-1">
                      <li v-for="item in row.evidence" :key="item.id" class="text-xs leading-4 text-growth-800">{{ item.text }}</li>
                    </ul>
                    <span v-else class="text-xs text-growth-600">No excerpt available</span>
                    <router-link :to="trackerLink(row, row.id)" class="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-growth-800 hover:underline">
                      <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="h-3 w-3" />Open in Engagement Tracker
                    </router-link>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import ET_TrajectoryBadge from '@/components/EngagementTracker/parts/ET_TrajectoryBadge.vue';
import ET_NextStep from '@/components/EngagementTracker/parts/ET_NextStep.vue';
import { typeLabel, severityStyle } from '@/components/EngagementTracker/engagementTrackerHelpers.js';

export default {
  components: { LoadingIndicator, ET_TrajectoryBadge, ET_NextStep },
  props: { engagementId: { type: [Number, String], required: true } },
  data() { return { rows: [], loading: false, error: '', controller: null, sort: { key: 'type', direction: 'asc' }, expandedRows: {} }; },
  computed: {
    tenantScope() { return this.$store.state.tenant.current_tenant_slug; },
    columns() { return [
      { key: 'type', label: 'Type', edgeClass: 'pl-4', sortable: true },
      { key: 'title', label: 'Finding', sortable: true },
      { key: 'status', label: 'Status · this meeting', sortable: true },
      { key: 'last_discussed', label: 'Track record', sortable: true },
      { key: 'details', label: '', edgeClass: 'pr-4', sortable: false },
    ]; },
    sortedRows() { return [...this.rows].sort((a, b) => String(a[this.sort.key] || '').localeCompare(String(b[this.sort.key] || '')) * (this.sort.direction === 'asc' ? 1 : -1)); },
  },
  watch: { engagementId: { immediate: true, handler: 'load' }, tenantScope: 'load' },
  beforeUnmount() { this.controller?.abort(); },
  methods: {
    typeLabel,
    severityStyle,
    label(value) { return (value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase()); },
    isExpanded(id) { return !!this.expandedRows[id]; },
    toggleExpanded(id) { this.expandedRows = { ...this.expandedRows, [id]: !this.expandedRows[id] }; },
    toggleSort(key) { this.sort = { key, direction: this.sort.key === key && this.sort.direction === 'asc' ? 'desc' : 'asc' }; },
    typeDotClass(type) { return ({ initiative: 'bg-primary-500', topic: 'bg-neutral-500', constraint: 'bg-red-dark' })[type] || 'bg-gray-500'; },
    changeLabel(change) { return ({ created: 'New', resolved: 'Resolved here', dropped: 'Dropped here', status_changed: 'Status changed', updated: 'Updated' })[change] || ''; },
    changeTitle(change) { return ({ created: 'First raised in this meeting', resolved: 'This meeting carried the resolving evidence', dropped: 'This meeting carried the dropping evidence', status_changed: 'Status changed in this meeting', updated: 'Existing finding, new evidence from this meeting' })[change] || ''; },
    changeClass(change) { return ({ created: 'bg-blue-100 text-blue-700', resolved: 'bg-secondary-100 text-green-dark', dropped: 'bg-red-tint text-red-dark', status_changed: 'bg-amber-100 text-amber-700', updated: 'bg-white border border-gray-300 text-gray-700' })[change] || 'bg-white border border-gray-300 text-gray-700'; },
    statusClass(status) { return ({ open: 'bg-growth-25 text-gray-700', in_progress: 'bg-neutral-50 text-neutral-900', resolved: 'bg-secondary-100 text-green-dark', dropped: 'bg-red-tint text-red-dark' })[status] || 'bg-growth-25 text-gray-700'; },
    trackerLink(row, nodeId) { return { path: `/entity/${row.entity_id}/engagement-tracker`, query: { node: nodeId } }; },
    relationshipLabel(rel) { const base = rel.edge_type.replaceAll('_', ' '); return rel.direction === 'in' ? `${base} by ` : `${base} `; },
    formatDate(value) { if (!value) return ''; return new Date(`${value}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); },
    monthYear(value) { if (!value) return ''; return new Date(`${value}T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); },
    discussionSummary(row) {
      const meetings = row.meeting_count === 1 ? '1 meeting' : `${row.meeting_count} meetings`;
      if (!row.first_discussed) return meetings;
      const first = this.monthYear(row.first_discussed);
      const last = this.monthYear(row.last_discussed);
      return first === last ? `${meetings} · ${first}` : `${meetings} · ${first} to ${last}`;
    },
    async load() {
      this.controller?.abort(); const controller = new AbortController(); const scope = this.tenantScope;
      this.controller = controller; this.rows = []; this.expandedRows = {}; this.loading = true; this.error = '';
      try { const { data } = await axios.get(`api/engagement-intelligence/${this.engagementId}/`, { signal: controller.signal }); if (this.controller === controller && this.tenantScope === scope) this.rows = data; }
      catch { if (!controller.signal.aborted && this.controller === controller && this.tenantScope === scope) this.error = 'Could not load meeting intelligence.'; }
      finally { if (this.controller === controller && this.tenantScope === scope) this.loading = false; }
    },
  },
};
</script>

<style scoped>
@reference '../../assets/theme.css';
.disclosure-button { @apply inline-flex h-6 w-6 items-center justify-center rounded text-growth-600 hover:bg-growth-25 hover:text-growth-900 focus:outline-none; }
</style>
